import {
  Membership,
  MembershipStatusEnum,
  Workspace,
} from '@sr-sdks/api-sdk-axios';

export interface EffectiveMembershipStatus {
  /** Convenience flag: resolved status is `CurrentMember`. */
  isCurrentMember: boolean;
  /** Resolved status the UI should treat as the source of truth. */
  membershipStatus: MembershipStatusEnum;
  /**
   * Whether the "Membership Status" UI block should render. Always true for
   * staff (they need to act on `NonMember` workspaces); for non-staff we hide
   * the block when the resolved status is `NonMember` to avoid noise.
   */
  showMembershipStatus: boolean;
}

interface UseEffectiveMembershipStatusOptions {
  isStaffView?: boolean;
}

/**
 * Membership statuses that represent a verdict issued by staff (approve /
 * deactivate / mark resigned / reject). Staff mutations flow through the
 * membership service, so the membership record reflects these verdicts first
 * and the workspace's cached `membershipStatus` can lag behind for a short
 * window until the workspace query refetches.
 *
 * When the membership record carries any of these statuses we trust it over
 * the (potentially stale) workspace cache, in both the upgrade direction
 * (e.g. approval flips `InReview` → `CurrentMember`) and the downgrade
 * direction (e.g. revocation flips `CurrentMember` → `InactiveMember` /
 * `ResignedMember` / `Rejected`).
 *
 * `RemovedMember` is included for safety while legacy data is being migrated
 * to `InactiveMember`.
 */
const STAFF_VERDICT_MEMBERSHIP_STATUSES: readonly MembershipStatusEnum[] = [
  MembershipStatusEnum.CurrentMember,
  MembershipStatusEnum.InactiveMember,
  MembershipStatusEnum.RemovedMember,
  MembershipStatusEnum.ResignedMember,
  MembershipStatusEnum.Rejected,
];

/**
 * Single source of truth for the membership status the UI should display.
 *
 * Resolution rules:
 * 1. If the membership record carries a staff verdict (see
 *    {@link STAFF_VERDICT_MEMBERSHIP_STATUSES}), trust it. The workspace's
 *    cached `membershipStatus` can lag behind staff mutations in either
 *    direction — an approval may not yet have propagated from the membership
 *    service to the workspace cache, and a revocation has the same race in
 *    reverse. Either way the membership record holds the fresher verdict, so
 *    we never want to show stale "current member" details after a downgrade
 *    or stale "membership required" copy after an approval.
 * 2. Staff view prioritises the membership status because staff mutations
 *    flow through the membership service.
 * 3. Non-staff view prioritises the workspace status because non-staff
 *    mutations (e.g. applying for membership) flow through the workspace
 *    service, so the workspace holds the fresher value for those transitions.
 * 4. Falls back to `NonMember` when neither side has a status.
 */
export const useEffectiveMembershipStatus = (
  workspace?: Workspace,
  membership?: Membership,
  options?: UseEffectiveMembershipStatusOptions,
): EffectiveMembershipStatus => {
  const isStaffView = options?.isStaffView ?? false;

  const membershipRecordStatus = membership?.membershipStatus;
  const staffVerdictFromMembership =
    membershipRecordStatus &&
    STAFF_VERDICT_MEMBERSHIP_STATUSES.includes(membershipRecordStatus)
      ? membershipRecordStatus
      : undefined;

  const membershipStatus: MembershipStatusEnum =
    staffVerdictFromMembership ??
    (isStaffView
      ? membershipRecordStatus ||
        workspace?.membershipStatus ||
        MembershipStatusEnum.NonMember
      : workspace?.membershipStatus ||
        membershipRecordStatus ||
        MembershipStatusEnum.NonMember);

  const isCurrentMember =
    membershipStatus === MembershipStatusEnum.CurrentMember;

  const showMembershipStatus =
    isStaffView || membershipStatus !== MembershipStatusEnum.NonMember;

  return {
    isCurrentMember,
    membershipStatus,
    showMembershipStatus,
  };
};
